import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authApi } from '@/api/auth';
import type { SessionUser } from '@/api/types';
import { STORAGE_KEY } from '@/utils/platform';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<SessionUser | null>(loadFromStorage());
  const fetchMeAttempted = ref(false);
  const isLogin = computed(() => !!user.value);
  const roleCodes = computed(() => user.value?.role_codes ?? []);
  const permissions = computed(() => user.value?.permissions ?? []);
  const orgName = computed(() => user.value?.org_name ?? '');
  const isSuperAdmin = computed(() => !!user.value?.is_super_admin);
  const isSuperAdminMode = computed(() => !!user.value?.is_super_admin && !user.value?.org_uuid);
  const isAdmin = computed(() => roleCodes.value.includes('admin'));


  function loadFromStorage(): SessionUser | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as SessionUser) : null;
    } catch (e) {
      console.error('[Auth] loadFromStorage JSON.parse failed', e);
      return null;
    }
  }

  function setUser(u: SessionUser | null) {
    user.value = u;
    if (u) {
      const { wx_openid, ...safeUserInfo } = u;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(safeUserInfo));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  let fetchMePromise: Promise<SessionUser | null> | null = null;

  async function fetchMe() {
    if (fetchMePromise) return fetchMePromise;
    fetchMePromise = (async () => {
      try {
        const u = await authApi.me();
        setUser(u);
        return u;
      } catch (e) {
        console.error('[Auth] fetchMe failed', e);
        setUser(null);
        return null;
      } finally {
        fetchMePromise = null;
      }
    })();
    return fetchMePromise;
  }

  async function logout() {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      fetchMeAttempted.value = false;
    }
  }

  function hasAnyRole(...codes: string[]) {
    if (!user.value) return false;
    if (codes.length === 0) return true;
    return codes.some((c) => roleCodes.value.includes(c));
  }

  function hasPermission(code: string) {
    if (!user.value) return false;
    return permissions.value.includes(code);
  }

  function hasAnyPermission(...codes: string[]) {
    if (!user.value) return false;
    if (codes.length === 0) return true;
    return codes.some((c) => permissions.value.includes(c));
  }

  async function switchOrg(org_uuid: string) {
    const u = await authApi.switchOrg(org_uuid);
    setUser(u);
    return u;
  }

  async function switchToSuperAdmin() {
    const u = await authApi.switchToSuperAdmin();
    setUser(u);
    return u;
  }

  return { user, isLogin, roleCodes, permissions, orgName, isSuperAdmin, isSuperAdminMode, isAdmin, fetchMeAttempted, fetchMe, logout, switchOrg, switchToSuperAdmin, hasAnyRole, hasPermission, hasAnyPermission, setUser };
});
